import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { isLuxPlusToursEnabled } from 'checkout/selectors/tourV2Selectors'
import TextButton from 'components/Luxkit/Button/TextButton'
import SolidCheckCircleIcon from 'components/Luxkit/Icons/solid/SolidCheckCircleIcon'
import SolidExclamationCircleIcon from 'components/Luxkit/Icons/solid/SolidExclamationCircleIcon'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import { mediaQueryOnly, mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import { useAppSelector } from 'hooks/reduxHooks'
import { pluralizeToString } from 'lib/string/pluralize'
import extractTourV2PurchasableOptionDetails from 'lib/tours/extractTourV2PurchasableOptionDetails'
import LuxPlusLabelWithModal from 'luxPlus/components/LuxPlusLabelWithModal'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import { rem } from 'polished'
import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import TourV2DepartureSoldOutTile from './TourV2DepartureSoldOutTile'
import { TOUR_V2_DEFAULT_OCCUPANCIES } from 'constants/tours'
import { checkPurchasableOptionHasPackageUpgradeOrRoomType } from 'lib/tours/tourUtils'
import { min, sum } from 'lib/array/arrayUtils'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Plural from 'components/utils/Formatters/Plural'
import FormatCurrency from 'components/Common/FormatCurrency'
import TourV2DeparturePricingDropdown from './TourV2DeparturePricingDropdown'
import generateDepartureRoomPriceBreakdown from 'lib/tours/generateDepartureRoomPriceBreakdown'
import { getDepartureOptionsForTravellers } from 'lib/tours/tourDepartureUtils'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { checkCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import TourV2DepartureTileDates from './TourV2DepartureTileDates'
import Tooltip from 'components/Luxkit/Tooltip'
import SolidInfoCircleIcon from 'components/Luxkit/Icons/solid/SolidInfoCircleIcon'
import { isAgentHubMultiCartItemsEnabled } from 'agentHub/selectors/agentHubSelectors'
import TourDepositTag from '../TourDepositTag'
import moment from 'moment'
import ModalContext from 'contexts/ModalContext'
import TourDepositInfoModal, { TourDepositInfoModalResult } from '../TourDepositInfoModal'
import PriceRowSale from 'components/Luxkit/PricePoints/PriceRowSale'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import noop from 'lib/function/noop'
import LuxLoyaltyPoints from 'luxLoyalty/components/LuxLoyaltyPoints'
import { generateLuxLoyaltyPointsCalculatorTourOptions } from 'luxLoyalty/lib/pointsCalculation/calculatorOptionsGenerators'

const Tile = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  display: grid;
  padding: ${rem(20)};
  gap: ${rem(16)};
  grid-template-areas:
  "dates"
  "tags"
  "member"
  "prices";

  ${mediaQueryUp.tablet} {
    column-gap: ${rem(40)};
    row-gap: ${rem(12)};
    padding: ${rem(16)} 0;
    grid-template-columns: 1fr ${rem(190)} min-content;
    grid-template-areas:
    "dates prices cta"
    "tags prices cta"
    "member prices cta";
    align-items: center;
  }

  ${mediaQueryUp.desktop} {
    grid-template-columns: ${rem(320)} 1fr ${rem(190)} min-content;
    grid-template-areas:
      "dates tags prices cta"
      "dates member prices cta";
  }

  ${mediaQueryUp.largeDesktop} {
    grid-template-columns: ${rem(320)} ${rem(180)} ${rem(210)} ${rem(190)} min-content;
    grid-template-areas: "dates tags member prices cta";
  }
`

const TagGridArea = styled(Group)`
  grid-area: tags;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: end;
  }
`

const MemberGridArea = styled(Group)`
  grid-area: member;

  /* We collapsed the columns, now we need to push them together as they would be centered and too far apart */
  ${mediaQueryOnly.desktop} {
    align-self: start;
  }
`

interface Props {
  offer: App.Tours.TourV2Offer;
  departure: App.Tours.TourV2OfferDeparture;
  travellers?: Array<App.Occupants>;
  variation: App.Tours.TourV2OfferVariation;
  onSelect?: (departure: App.Tours.TourV2OfferDeparture, keepBrowsing?: boolean) => void;
}

function TourV2DepartureTile(props: Props) {
  const {
    offer,
    departure,
    onSelect = noop,
    travellers = TOUR_V2_DEFAULT_OCCUPANCIES,
    variation,
  } = props

  const showModal = useContext(ModalContext)

  const luxPlusEnabled = useAppSelector(isLuxPlusToursEnabled)
  const isLuxPlusMember = useAppSelector(checkCanViewLuxPlusBenefits)

  const views = useMemo(() => getDepartureOptionsForTravellers(departure, travellers), [travellers, departure])
  const breakdowns = useMemo(() => views.map(view => {
    return generateDepartureRoomPriceBreakdown(view.room, view.cheapestOption, offer)
  }), [offer, views])

  const canViewLuxPlusBenefits = useAppSelector(checkCanViewLuxPlusBenefits)
  const total = useMemo(() => sum(breakdowns, breakdown => {
    if (canViewLuxPlusBenefits) {
      return breakdown.totalMemberPrice || breakdown.totalPrice
    }
    return breakdown.totalPrice
  }), [breakdowns, canViewLuxPlusBenefits])

  const cheapestOption = useMemo(() => min(views, view => view.cheapestOption.price)?.cheapestOption, [views])
  const onSelectClick = useCallback(() => {
    onSelect(departure)
  }, [departure, onSelect])
  const onAddToCartClick = useCallback(() => {
    onSelect(departure, true)
  }, [departure, onSelect])

  const details = useMemo(() => cheapestOption ? extractTourV2PurchasableOptionDetails(cheapestOption) : undefined, [cheapestOption])

  const { hasPackageUpgrade, hasRoomUpgrade } = useMemo(() => {
    return checkPurchasableOptionHasPackageUpgradeOrRoomType(views.flatMap(view => view.options))
  }, [views])

  const hasDiscount = useMemo(() => {
    return views.flatMap(view => view.options).some(option => extractTourV2PurchasableOptionDetails(option).hasDiscount)
  }, [views])

  const tourV2StartLocation = useMemo(() => [offer.variations[0]?.startLocation ?? ''], [offer])
  const totalMemberSavings = useMemo(() => {
    const memberTotal = sum(breakdowns, breakdown => breakdown.totalMemberPrice)
    const nonMembmerTotal = sum(breakdowns, breakdown => breakdown.totalPrice)
    return memberTotal > 0 ? nonMembmerTotal - memberTotal : 0
  }, [breakdowns])

  const isDepositAvailable = useMemo<boolean>(() => {
    if (!offer.paymentScheduleTemplate) return false
    const departureDate = moment(departure.startDate)
    const daysTillDeparture = departureDate.diff(moment(), 'days')
    return daysTillDeparture >= offer.paymentScheduleTemplate.minimumLeadDays
  }, [departure, offer])

  const handleTourDepositInfoClick = useCallback(async() => {
    if (offer.paymentScheduleTemplate) {
      const result = await showModal<TourDepositInfoModalResult>(<TourDepositInfoModal
        paymentScheduleTemplate={offer.paymentScheduleTemplate}
        primaryActionText="Select departure"
      />)
      if (result === 'primary-action') {
        onSelectClick()
      }
    }
  }, [offer.paymentScheduleTemplate, onSelectClick, showModal])

  const pointsEarnCalculationOptions = useMemo<Array<App.LuxLoyaltyPointsEarnCalculationRequest | undefined>>(
    () => {
      return [generateLuxLoyaltyPointsCalculatorTourOptions(offer, { price: cheapestOption?.price, memberPrice: cheapestOption?.memberPrice, roomType: cheapestOption?.roomType })]
    },
    [cheapestOption, offer],
  )

  if (!cheapestOption || !details) {
    return <TourV2DepartureSoldOutTile
      departure={departure}
      offer={offer}
      travellers={travellers}
    />
  }

  return <Tile>
    <Group direction="vertical" gap={12}>
      <TourV2DepartureTileDates departure={departure} gridArea="dates" />
      <LuxLoyaltyPoints calculationRequests={pointsEarnCalculationOptions} calculationType="estimate" />
    </Group>
    <TagGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      tabletGap={4}
      wrap="wrap"
    >
      {departure.isGuaranteed && <CaptionTextBlock
        variant="medium"
        weight="bold"
        startIcon={<SolidCheckCircleIcon colour="secondary-normal"/>}
        endIcon={<Tooltip description="Guaranteed with 2 passengers">
          <SolidInfoCircleIcon colour="neutral-two" />
        </Tooltip>}
      >
        Guaranteed departure
      </CaptionTextBlock>}
      {cheapestOption.inventoryLeft <= 4 && <CaptionTextBlock
        variant="medium"
        weight="bold"
        startIcon={<SolidExclamationCircleIcon colour="urgency"/>}
      >
        {pluralizeToString('spot', cheapestOption.inventoryLeft)} left
      </CaptionTextBlock>}
      {(hasPackageUpgrade || hasRoomUpgrade) && <CaptionTextBlock
        variant="medium"
        weight="bold"
        startIcon={<SolidCheckCircleIcon colour="secondary-normal"/>}
      >
        Upgrades available
      </CaptionTextBlock>}
    </TagGridArea>
    <MemberGridArea
      direction="horizontal"
      desktopDirection="vertical"
      gap={8}
      desktopGap={4}
      wrap="wrap"
    >
      {luxPlusEnabled && totalMemberSavings > 0 && <LuxPlusLabelWithModal
        offer={offer}
        saveAmount={totalMemberSavings}
        type="member-price-offer"
        hideSaveAmountPlus
      />}
      {isDepositAvailable && <TourDepositTag offer={offer} onInfoClick={handleTourDepositInfoClick} />}
      {hasDiscount && <TourDiscountLabel />}
    </MemberGridArea>
    <Group
      style={{ gridArea: 'prices' }}
      direction="horizontal"
      gap={12}
      verticalAlign="end"
      horizontalAlign="space-between"
    >
      <VerticalSpacer gap={4}>
        <div>
          <PriceRowPriceCaption>From</PriceRowPriceCaption>
          <LuxPlusPriceStack
            size="L"
            pricing={details.pricing}
            memberPricing={details.memberPricing}
            isTour
          />
          {details.hasDiscount && !(canViewLuxPlusBenefits && details.memberPrice) && <PriceRowSale value={details.fullPricePerPerson} />}
          <PriceRowAgentHubCommission
            size="M"
            kind="plain"
            offerLocations={tourV2StartLocation}
            offerId={offer.id}
            productType={offer.productType}
          />
          {!!details.valuedAt && <PriceRowValueDiscountWithCaption
            size="M"
            originalValue={details.valuedAt.price}
            discountPercentage={isLuxPlusMember ? details.valuedAt.memberSavingsPercentage : details.valuedAt.savingPercentage}
          />}
          <PriceRowCaption>
            {travellers.length <= 1 && details.roomTypeName}
            {travellers.length > 1 && <>
              <Plural count={travellers.length} singular="room" withCount /> · <FormatCurrency value={total} /> total
            </>}
          </PriceRowCaption>
        </div>
        {travellers.length > 1 && <TourV2DeparturePricingDropdown
          travellers={travellers}
          departure={departure}
          offer={offer}
          variation={variation}
        /> }
      </VerticalSpacer>
      <CSSBreakpoint max="mobile">
        <Group direction="vertical" gap={8}>
          <TextButton kind="primary" onClick={onSelectClick}>Select</TextButton>
          {isAgentHubMultiCartItemsEnabled && (
            <TextButton
              kind="secondary"
              onClick={onAddToCartClick}
            >
              Add to cart
            </TextButton>
          )}
        </Group>
      </CSSBreakpoint>
    </Group>
    <CSSBreakpoint min="tablet" style={{ gridArea: 'cta' }}>
      <Group direction="vertical" gap={8}>
        <TextButton kind="primary" onClick={onSelectClick} data-testid="departure-book-button">Select</TextButton>
        {isAgentHubMultiCartItemsEnabled && (
          <TextButton
            kind="secondary"
            onClick={onAddToCartClick}
          >
            Add to cart
          </TextButton>
        )}
      </Group>
    </CSSBreakpoint>
  </Tile>
}

export default React.memo(TourV2DepartureTile)
